'use client';

import './styles/default.scss';
import { TickerPill } from '@fanduel/casino-swj-consumer';
import { useWinFrequency } from '../../hooks/use-win-frequency';
import { useOptInManager } from '../../hooks/use-opt-in-manager';
import { SiteWideJackpotData } from '@/types/aggregation/site-wide-jackpot';
import { useSwjRulesNavigation } from '@/app/components/site-wide-jackpot/hooks/use-swj-rules-navigation';
import { useStore } from '@/store/store';
import { useAccountUm } from '@/hooks/use-account-um';
import { POPUPS } from '@/app/components/popup/constants';
import { useEffect, useMemo } from 'react';
import { updateCustomerPreferences } from '@/lib/customer-preference';
import { mapSWJInfo, mapMultipliers } from '../../transformers/site-wide-jackpot.transformer';
import { getSwjTrackerToggleProps } from '@/tracking/amplitude/events/general/swj-opt-in';
import { useShallow } from 'zustand/react/shallow';
import { useGlobalContext } from '@/store/contexts/global-context';
import { usePathname } from 'next/navigation';

type TickerPillProps = {
    jackpotData: SiteWideJackpotData;
};

export const TickerPillComponent: React.FC<TickerPillProps> = ({ jackpotData }) => {
    const { triggerLogin } = useAccountUm();
    const { isLoggedIn, swjTickerRedesignVariant, isAndroidWrapper, isMobile } = useGlobalContext();
    const jackpotId = jackpotData?.provider_jackpot_id?.[0]?.text;
    const { tierWinFrequency } = useWinFrequency(jackpotId);
    const { isOptedIn, isLoading } = useOptInManager();
    const { queuePopup, closePopup, updatePopupParams, activePopup } = useStore((s) => s.popup);
    const mappedData = mapSWJInfo(jackpotData);
    const showTickerPill = swjTickerRedesignVariant === 'variant-1';
    const openInBrowser = !!mappedData?.openRulesExternally && isAndroidWrapper;
    const multipliers = mappedData?.multipliers ?? [];
    const shortRules = mappedData?.dybShortRules ?? '';
    const logoUrl = mappedData?.alternateLogo ?? '';
    const currentPath = usePathname() || '/';
    const { showDYBTracker, setDYBUserPreferences } = useStore((s) => s.siteWideJackpot);
    const { loading, updatedTiers, multiplier } = useStore(
        useShallow((state) => ({
            activeIndex: state.siteWideJackpot.activeIndex,
            loading: state.siteWideJackpot.loading,
            updatedTiers: state.siteWideJackpot.updatedTiers,
            multiplier: state.siteWideJackpot.multiplier
        }))
    );
    const { sendEvent } = useStore((s) => s.tracking);

    const { handleRulesClick } = useSwjRulesNavigation({
        openInBrowser,
        rulesPath: mappedData?.rulesPath,
        navigationSource: 'category'
    });

    const isDataLoading = loading || isLoading || updatedTiers.length === 0;

    const params = useMemo(() => {
        const jackpotMultipliers = mapMultipliers(multipliers);

        return {
            jackpotLogoUrl: logoUrl,
            shortRules,
            multipliers: jackpotMultipliers,
            jackpotId,
            support: mappedData?.rulesPath,
            showTickerPill,
            showTickerPillPreference: !!showDYBTracker,
            handleTickerPillToggle: () => {
                const next = !showDYBTracker;
                sendEvent([
                    {
                        destination: 'amplitude',
                        eventProps: getSwjTrackerToggleProps(
                            multiplier,
                            updatedTiers,
                            next ? 'show_tracker' : 'hide_tracker'
                        )
                    }
                ]);
                setDYBUserPreferences({ showDYBTracker: next });
                updateCustomerPreferences({ showDYBTracker: next });
            },
            handleJackpotRules: () => {
                handleRulesClick();
                closePopup();
            }
        };
    }, [
        closePopup,
        handleRulesClick,
        jackpotId,
        logoUrl,
        mappedData?.rulesPath,
        multiplier,
        multipliers,
        shortRules,
        showDYBTracker,
        showTickerPill,
        setDYBUserPreferences,
        sendEvent,
        updatedTiers
    ]);

    useEffect(() => {
        if (activePopup?.type === POPUPS.SWJ_OPT_IN) {
            updatePopupParams(activePopup.id, { swjParams: params });
        }
    }, [activePopup?.type, activePopup?.id, updatePopupParams, params]);

    const togglePopup = (value: boolean) => {
        if (!value) {
            return closePopup();
        }

        return queuePopup({
            type: POPUPS.SWJ_OPT_IN,
            params: { swjParams: params, module: 'floating_button' }
        });
    };

    const handleTickerPillClick = () => {
        if (!isLoggedIn) {
            triggerLogin();
        } else {
            togglePopup(true);
        }
    };

    const shouldHideTickerPill = useMemo(() => {
        if (isLoggedIn && (showDYBTracker === undefined || !showDYBTracker)) return true;
        if (currentPath.includes('/c/')) {
            return (
                currentPath.includes('double-your-bet') ||
                currentPath.includes('swj') ||
                currentPath.includes('dyb') ||
                currentPath.includes('site-wide-jackpot')
            );
        }
        return currentPath !== '/';
    }, [currentPath, isLoggedIn, showDYBTracker]);

    // Tiers are shuffled in reverse order for the animation loop — the last element is
    // duplicated at the end so the transition back to the first tier appears seamless.
    const animatedTiers =
        updatedTiers.length > 0 ? [...[...updatedTiers].reverse(), updatedTiers[updatedTiers.length - 1]] : [];

    return !showTickerPill ? null : (
        <TickerPill
            handleClick={handleTickerPillClick}
            loading={isDataLoading}
            tiers={animatedTiers}
            isOptedIn={isOptedIn}
            tierWinFrequency={tierWinFrequency}
            jackpotData={jackpotData}
            mobileBottomBarEnabled={isMobile}
            isHidden={shouldHideTickerPill}
            brand={process.env.BRAND}
        />
    );
};
